import React from 'react';
import { I18n } from '@aws-amplify/core';

import { Row, Col, Spin } from 'antd';
import { tableColumns } from './TableColumns';
import { useHandleReports } from '../../../hooks/useHandleReports';
import type { ReportData } from '@/components/Settings/DataExtraction/ExtractionType/Hierarchy/types';
import * as S from '@/components/Settings/DataExtraction/styles';

interface TableListFormProps {
	data?: ReportData[];
	loading?: boolean;
	selectedReportIds?: string[];
	totalReportsSelected?: number;
}

export function TableListForm({
	data = [],
	loading,
	selectedReportIds = [],
	totalReportsSelected
}: Readonly<TableListFormProps>) {
	const {
		isSelectAllMode,
		isUpdatingReport,
		isCreatingReport,
		isDeletingReport,
		handleSelectAllClick,
		handleDeselectAllClick,
		handleCreateReports,
		handleDeleteReports,
		handleCreateReportsBatch,
		handleDeleteReportsBatch
	} = useHandleReports();

	const currentPageIds = data.map((item) => item.id);
	const checkedIds = selectedReportIds;
	const finalSelectedRowKeys = checkedIds.filter((id: string) => currentPageIds.includes(id));

	function handleRowSelect(record: ReportData, selected: boolean) {
		if (selected) return handleCreateReports(record.id, record.report_type);
		handleDeleteReports(record.id, record.report_type);
	}

	function buildCreatePayload(rows: ReportData[]) {
		return {
			reportIds: rows.map((row) => row.id),
			reportType: rows[0]?.report_type
		};
	}

	function buildDeletePayload() {
		return {
			reportIds: finalSelectedRowKeys,
			reportType: data[0]?.report_type
		};
	}

	function handleSelectAllRows(isSelected: boolean, affectedRows: ReportData[]) {
		if (isSelected) {
			const { reportIds, reportType } = buildCreatePayload(affectedRows);
			handleCreateReportsBatch(reportIds, reportType);
			return;
		}

		const { reportIds, reportType } = buildDeletePayload();
		handleDeleteReportsBatch(reportIds, reportType);
	}

	return (
		<Spin spinning={loading || isUpdatingReport || isCreatingReport || isDeletingReport}>
			<S.FormItemWrapper>
				<Row justify="space-between" align="middle" gutter={[0, 24]}>
					<Col>
						<S.LabelSmallRegular>
							{totalReportsSelected} {I18n.get('Selected')}
						</S.LabelSmallRegular>
					</Col>
					<Col>
						<S.LabelSmallRegularLink
							onClick={isSelectAllMode ? handleDeselectAllClick : handleSelectAllClick}
						>
							{isSelectAllMode ? I18n.get('Deselect all') : I18n.get('Select all')}
						</S.LabelSmallRegularLink>
					</Col>
					<S.CustomTable
						rowKey="id"
						columns={tableColumns()}
						dataSource={data}
						loading={false}
						pagination={false}
						tableLayout="fixed"
						rowSelection={{
							type: 'checkbox',
							selectedRowKeys: finalSelectedRowKeys,
							onSelect: handleRowSelect,
							onSelectAll: handleSelectAllRows,
							getCheckboxProps: () => ({
								disabled: isSelectAllMode
							})
						}}
					/>
				</Row>
			</S.FormItemWrapper>
		</Spin>
	);
}
